/**
 * FunnelGuard — auth + step routing for the /start/* purchase funnel.
 *
 * Wraps AuthGuard (Clerk session + /users/me) and then fetches
 * GET /funnel/state to decide whether the requested step is reachable.
 *
 * Redirect matrix (forward-only blocking; staying on the current step or
 * going back to an earlier one is allowed — except after purchase):
 *
 *   funnel state                          target step    allowed steps
 *   ─────────────────────────────────────────────────────────────────────
 *   no intent                             account        account
 *   intent, agreement not accepted        agreement      account, agreement
 *   agreement accepted, no open order     products       account…payment
 *   pending_payment order (Stripe         payment        account…payment,
 *   checkout in flight or abandoned)                     confirmation
 *   paid order                            confirmation   confirmation only
 *
 * Shipping + Payment both sit ahead of products in the "agreement accepted"
 * state because the plan/add-on selection AND the shipping address are
 * client-side (sessionStorage) — the ShippingPage and PaymentPage each bounce
 * back (to /start/products, or /start/shipping for a physical order missing an
 * address) when their prerequisite is absent. After purchase, every step
 * redirects to confirmation: checkout is
 * idempotent and v1 allows one purchase per account, so earlier steps are
 * no longer sensible.
 *
 * Design: docs/technical-design-website-product.md §4.1 (review F3/F8)
 */
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { Button } from '@/components/ui/button';
import { useFunnelState } from '@/hooks/useFunnel';
import { FUNNEL_STEP_ORDER, FUNNEL_STEP_PATHS, type FunnelStep } from '@/lib/funnel';
import type { FunnelState } from '@/types';

interface FunnelGuardProps {
  step: FunnelStep;
  children: React.ReactNode;
}

/** The furthest step the user has legitimately reached. */
export function funnelTargetStep(state: FunnelState): FunnelStep {
  if (state.latestOrder?.status === 'paid') {
    return 'confirmation';
  }
  // A pending_payment order is an in-flight (or abandoned) Stripe checkout —
  // the user must be able to come back and retry payment, NOT be locked to
  // the confirmation step.
  if (state.latestOrder?.status === 'pending_payment') {
    return 'payment';
  }
  if (state.agreementAccepted) return 'products';
  if (state.intent) return 'agreement';
  return 'account';
}

/** Where to send the user if the requested step is not reachable, else null. */
export function funnelRedirectFor(step: FunnelStep, state: FunnelState): string | null {
  const target = funnelTargetStep(state);

  // Post-purchase the funnel is locked to the confirmation step.
  if (target === 'confirmation') {
    return step === 'confirmation' ? null : FUNNEL_STEP_PATHS.confirmation;
  }

  // While a payment is in flight, Stripe's success URL lands on confirmation
  // BEFORE the webhook flips the order to paid — that step must stay
  // reachable (it shows the "finalizing payment" state and verifies).
  if (target === 'payment' && step === 'confirmation') {
    return null;
  }

  // Selection happens client-side, so payment is reachable once the
  // agreement is accepted (PaymentPage self-guards on a missing selection).
  const maxAllowed: FunnelStep = target === 'products' ? 'payment' : target;

  if (FUNNEL_STEP_ORDER[step] > FUNNEL_STEP_ORDER[maxAllowed]) {
    return FUNNEL_STEP_PATHS[target];
  }
  return null;
}

export function FunnelGuard({ step, children }: FunnelGuardProps) {
  return (
    <AuthGuard>
      <FunnelStepCheck step={step}>{children}</FunnelStepCheck>
    </AuthGuard>
  );
}

function FunnelStepCheck({ step, children }: FunnelGuardProps) {
  const { data: state, isLoading, isError, error, refetch } = useFunnelState();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isError || !state) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex max-w-md flex-col items-center gap-4 px-4 text-center">
          <p className="text-destructive">
            {error instanceof Error ? error.message : 'Failed to load your progress.'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const redirect = funnelRedirectFor(step, state);
  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  return <>{children}</>;
}
